import { ReactElement } from 'react'
import styled from 'styled-components'
import { ISelectCheckboxListProps, ISelectCheckboxOptionProps } from './types'
import { TSelectOption } from 'types/index'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { ErrorMessage } from 'components/v2/ErrorMessage'

const Input = styled.div`
  border-radius: 3px;
  border: 1px solid rgb(153, 153, 189);
  max-height: 195px;
  overflow-y: auto;
`
const SyledOption = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 15px;
  background-color: rgb(255, 255, 255);
  color: inherit;
  align-items: center;
  cursor: pointer;
  &:hover {
    background-color: rgb(242, 242, 242);
  }
`
const StyledCheckbox = styled.input`
  cursor: pointer;
`
const StyledLabel = styled.label`
  cursor: pointer;
  user-select: none;
`
const SelectCheckboxOption = ({ id, value, checked, onChange }: ISelectCheckboxOptionProps): ReactElement => {
  return (
    <SyledOption onClick={(): void => onChange(!checked)}>
      <StyledCheckbox type="checkbox" id={`option-${id}`} value={value} checked={checked} />
      <StyledLabel>{value}</StyledLabel>
    </SyledOption>
  )
}
const messages = defineMessages({
  selectAll: {
    id: 'selectCheckboxList.selectAll',
    defaultMessage: 'Select All'
  }
})
const StyledErrorMessage = styled.div`
  margin-top: 5px;
`

const SelectCheckboxList = <TSelectCheckboxListOption extends TSelectOption>({
  onChange,
  items,
  values,
  hasAllOption = false,
  getOptionLabel,
  emptyMessage,
  errors,
  touched
}: ISelectCheckboxListProps<TSelectCheckboxListOption>): ReactElement => {
  const intl = useIntl()
  const options = items.map((item) => {
    const isChecked = values.find((option) => option.id === item.id) ? true : false
    return {
      ...item,
      checked: isChecked
    }
  })
  const handleChange = (id: string): void => {
    const newOptions = options.map((option) => {
      if (option.id === id) {
        return { ...option, checked: !option.checked }
      } else {
        return option
      }
    })

    onChange(newOptions.filter((option) => option.checked))
  }

  const handleSelectAll = (value: boolean): void => {
    const newOptions = options.map((option) => ({ ...option, checked: value }))
    onChange(newOptions.filter((option) => option.checked))
  }

  const getOptionName = (option: TSelectCheckboxListOption): string => {
    return option?.name || ''
  }

  if (items.length === 0) {
    return (
      <SyledOption>
        {emptyMessage ? emptyMessage : <FormattedMessage id="selectCheckboxList.empty" defaultMessage="No items" />}
      </SyledOption>
    )
  }
  const showError = errors && touched
  return (
    <>
      <Input>
        {options.length > 0 && hasAllOption && (
          <SelectCheckboxOption
            id={'all'}
            value={intl.formatMessage(messages.selectAll)}
            checked={options.every((option) => option.checked)}
            onChange={(checked: boolean): void => handleSelectAll(checked)}
          />
        )}
        {options.map((option) => (
          <SelectCheckboxOption
            key={option.id}
            id={String(option.id)}
            value={getOptionLabel ? getOptionLabel(option) : getOptionName(option)}
            checked={option.checked}
            onChange={(): void => handleChange(option.id)}
          />
        ))}
      </Input>
      {showError && (
        <StyledErrorMessage>
          <ErrorMessage>{errors}</ErrorMessage>
        </StyledErrorMessage>
      )}
    </>
  )
}

export default SelectCheckboxList
